import { ApplicationRef, inject, Injectable, signal, Signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { concat, first, interval, Observable } from 'rxjs';
import { Platform, ToastController } from '@ionic/angular/standalone';
import { environment } from 'src/environments/environment';
import * as Sentry from '@sentry/angular';

const CACHE_INTEGRITY_KEY: string = 'cache-integrity-retries';
const INTEGRITY_RELOADING_KEY: string = 'cache-integrity-reloading';
const FORCE_RELOAD_KEY: string = 'pwa-force-reload';
const MAX_INTEGRITY_RETRIES: number = 2;
const UPDATE_CHECK_TIMEOUT_MS: number = 15000;

interface NgswManifest {
  assetGroups?: NgswAssetGroup[];
  appData?: unknown;
}

export interface LatestAvailableAppData {
  version?: string;
  buildTime?: string;
  env?: string;
}

interface NgswAssetGroup {
  name: string;
  installMode: string;
  urls: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PwaUpdateService {
  private readonly swUpdate: SwUpdate = inject(SwUpdate);
  private readonly appRef: ApplicationRef = inject(ApplicationRef);
  private readonly platform: Platform = inject(Platform);
  private readonly toastController: ToastController = inject(ToastController);

  private readonly updateCheckInterval: number = 300000;
  private isUpdateInstalling: boolean = false;

  private readonly _isChecking = signal<boolean>(false);
  readonly isChecking: Signal<boolean> = this._isChecking.asReadonly();

  constructor() {
    if (this.swUpdate.isEnabled) {
      this.initializeUpdateChecks();
      this.listenForUpdates();
    }
  }

  private initializeUpdateChecks(): void {
    const appIsStable$: Observable<boolean> = this.appRef.isStable.pipe(
      first((isStable: boolean) => isStable === true)
    );
    const everyFiveMinutes$: Observable<number> = interval(this.updateCheckInterval);
    const everyFiveMinutesOnceAppIsStable$: Observable<boolean | number> = concat(appIsStable$, everyFiveMinutes$);

    everyFiveMinutesOnceAppIsStable$.subscribe(async () => {
      try {
        await this.checkForUpdateWithTimeout({ notifyUser: false, source: 'interval' });
      } catch {
        // Periodic check failure is non-critical
      }
    });

    if (this.platform.is('capacitor') || this.platform.is('cordova')) {
      this.platform.resume.subscribe(async () => {
        try {
          await this.checkForUpdateWithTimeout({ notifyUser: false, source: 'resume' });
        } catch {
          // Update check failure is non-critical
        }
        try {
          await this.verifyCacheIntegrity();
        } catch {
          // Integrity check failure is non-critical
        }
      });
    }

    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden) {
        try {
          await this.checkForUpdateWithTimeout({ notifyUser: false, source: 'visibility' });
        } catch {
          // Update check failure is non-critical
        }
        try {
          await this.verifyCacheIntegrity();
        } catch {
          // Integrity check failure is non-critical
        }
      }
    });
  }

  private listenForUpdates(): void {
    this.swUpdate.versionUpdates.subscribe((evt: VersionEvent) => {
      if (evt.type === 'VERSION_DETECTED') {
        this.isUpdateInstalling = true;
      } else if (evt.type === 'VERSION_READY') {
        this.forceUpdate();
      } else if (evt.type === 'VERSION_INSTALLATION_FAILED') {
        this.isUpdateInstalling = false;
      }
    });

    this.swUpdate.unrecoverable.subscribe(async (event) => {
      Sentry.captureException(new Error(`SW unrecoverable: ${event.reason}`), {
        tags: { swUnrecoverable: true },
      });

      const toast: HTMLIonToastElement = await this.toastController.create({
        message: 'Restoring the latest version…',
        duration: 1500,
        position: 'top',
        color: 'primary',
      });
      await toast.present();

      this.clearCacheAndReload();
    });
  }

  private async forceUpdate(): Promise<void> {
    const toast: HTMLIonToastElement = await this.toastController.create({
      message: 'Updating to latest version...',
      duration: 1500,
      position: 'top',
      color: 'primary',
    });
    await toast.present();

    await this.activateUpdate();
  }

  private async activateUpdate(): Promise<void> {
    try {
      const controllerChanged: Promise<void> = new Promise<void>((resolve) => {
        navigator.serviceWorker.addEventListener(
          'controllerchange',
          () => resolve(),
          { once: true },
        );
      });

      const controllerTimeout: Promise<void> = new Promise<void>((resolve) => setTimeout(resolve, 5000));

      await this.swUpdate.activateUpdate();
      await Promise.race([controllerChanged, controllerTimeout]);

      const cacheNames: string[] = await caches.keys();
      await Promise.all(cacheNames.map((name: string) => caches.delete(name)));

      sessionStorage.setItem(FORCE_RELOAD_KEY, 'true');
      window.location.reload();
    } catch {
      await this.clearCacheAndReload();
    }
  }

  /**
   * Routes every SwUpdate.checkForUpdate() call through a 15s timeout tripwire.
   * The Angular SW update protocol can hang silently (never resolve/reject) if the
   * registered worker never answers the ngsw-protocol message — this happened for
   * real when sw-custom.js was push-only with no ngsw merged in. A timeout here
   * guarantees the caller always gets a settled promise, and a Sentry tag records
   * the triggering source so a silent hang is visible in monitoring, not just UX.
   * `notifyUser` is not branched on internally: whether a toast is shown is decided
   * by whichever call site owns the surrounding try/catch (background sources never
   * show one; the manual path's existing catch does) — it's carried here so every
   * call site self-documents its intent.
   */
  private async checkForUpdateWithTimeout(options: { notifyUser: boolean; source: string }): Promise<boolean> {
    let timedOut: boolean = false;
    let timeoutId!: ReturnType<typeof setTimeout>;

    const timeout$: Promise<never> = new Promise<never>((_resolve, reject) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        reject(new Error(`SW update check timed out (${options.source})`));
      }, UPDATE_CHECK_TIMEOUT_MS);
    });

    try {
      return await Promise.race([this.swUpdate.checkForUpdate(), timeout$]);
    } catch (error) {
      if (timedOut) {
        this.reportUpdateCheckTimeout(options.source);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Kept as its own method (rather than inlined) so tests can spy on it directly:
  // @sentry/angular's named exports compile to non-configurable getters under this
  // project's webpack/Karma setup, so `spyOn(Sentry, 'captureException')` cannot
  // intercept the call — see pwa-update.service.spec.ts.
  private reportUpdateCheckTimeout(source: string): void {
    Sentry.captureException(new Error(`SW update check timed out (${source})`), {
      tags: { swCheckTimeout: true, source },
    });
  }

  async checkForUpdate(): Promise<void> {
    // Reentrancy guard: the UI buttons are disabled via isChecking, but a direct second call
    // would otherwise let the first completion flip the signal to false while the second check
    // is still in flight. One manual check at a time; its toasts surface the result globally.
    if (this._isChecking()) {
      return;
    }
    this._isChecking.set(true);
    try {
      if (!this.swUpdate.isEnabled) {
        // A user pressing the button must always get feedback, never a silent no-op.
        throw new Error('SW update check unavailable: service worker disabled');
      }

      const updateFound: boolean = await this.checkForUpdateWithTimeout({ notifyUser: true, source: 'manual' });
      if (!updateFound) {
        const reloadTriggered: boolean = await this.verifyCacheIntegrity();
        if (!reloadTriggered) {
          const toast: HTMLIonToastElement = await this.toastController.create({
            message: 'You are on the latest version',
            duration: 2000,
            position: 'bottom',
            color: 'success',
          });
          await toast.present();
        }
      }
    } catch {
      const toast: HTMLIonToastElement = await this.toastController.create({
        message: 'Failed to check for updates',
        duration: 2000,
        position: 'bottom',
        color: 'danger',
      });
      await toast.present();
    } finally {
      this._isChecking.set(false);
    }
  }

  async verifyCacheIntegrity(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) return false;
    if (this.isUpdateInstalling) return false;

    if (sessionStorage.getItem(FORCE_RELOAD_KEY)) {
      sessionStorage.removeItem(FORCE_RELOAD_KEY);
      return false;
    }

    if (!navigator.serviceWorker.controller) return false;

    const retries: number = this.getIntegrityRetryCount();
    if (retries >= MAX_INTEGRITY_RETRIES) return false;

    try {
      const response: Response = await fetch('/ngsw.json?integrity-check=' + Date.now());
      if (!response.ok) return false;

      const manifest: NgswManifest = await response.json();
      const prefetchGroup: NgswAssetGroup | undefined = manifest.assetGroups?.find(
        (group: NgswAssetGroup) => group.installMode === 'prefetch',
      );
      if (!prefetchGroup?.urls?.length) return false;

      const expectedCount: number = prefetchGroup.urls.length;
      const allCacheNames: string[] = await caches.keys();
      const ngswCaches: string[] = allCacheNames.filter(
        (name: string) => name.startsWith('ngsw:') && name.includes(`:assets:${prefetchGroup.name}:`),
      );

      if (ngswCaches.length === 0) return false;

      let cachedCount: number = 0;
      for (const cacheName of ngswCaches) {
        const cache: Cache = await caches.open(cacheName);
        const keys: readonly Request[] = await cache.keys();
        cachedCount += keys.length;
      }

      const completionRatio: number = expectedCount > 0 ? cachedCount / expectedCount : 1;

      if (completionRatio < 0.5) {
        Sentry.captureException(
          new Error(`SW cache incomplete: ${cachedCount}/${expectedCount} assets cached`),
          { tags: { cacheIntegrity: true, completionRatio } },
        );
        this.setIntegrityRetryCount(retries + 1);
        sessionStorage.setItem(INTEGRITY_RELOADING_KEY, 'true');
        await this.clearCacheAndReload();
        return true;
      }
    } catch {
      // Integrity check failure is non-critical
    }

    return false;
  }

  async clearCacheAndReload(): Promise<void> {
    try {
      const cacheNames: string[] = await caches.keys();
      await Promise.all(cacheNames.map((cacheName: string) => caches.delete(cacheName)));

      const registrations: readonly ServiceWorkerRegistration[] =
        await navigator.serviceWorker.getRegistrations();
      await Promise.all(
        registrations.map((registration: ServiceWorkerRegistration) => registration.unregister()),
      );

      const url: URL = new URL(window.location.href);
      url.searchParams.set('cache-bust', Date.now().toString());
      window.location.href = url.toString();
    } catch {
      window.location.reload();
    }
  }

  getCurrentVersion(): string {
    return environment.appVersion || 'unknown';
  }

  /**
   * Fetches the appData stamped into the currently-deployed ngsw.json — i.e. what's
   * actually live on the server right now, independent of what this client has
   * installed. Used to show a "Latest available: vX.Y.Z" hint when it differs from
   * this build. Always network-fresh (cache: 'no-store' + cache-busting query param)
   * and never throws: missing manifest, non-2xx, malformed JSON, or a missing/invalid
   * appData shape all degrade quietly to null so callers can omit the UI rather than
   * show an alarming "Unavailable" state.
   */
  async getLatestAvailableAppData(): Promise<LatestAvailableAppData | null> {
    try {
      const response: Response = await fetch(`/ngsw.json?app-data=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return null;

      const manifest: NgswManifest = await response.json();
      if (!manifest.appData || typeof manifest.appData !== 'object') return null;

      const rawAppData: Record<string, unknown> = manifest.appData as Record<string, unknown>;
      const result: LatestAvailableAppData = {};
      if (typeof rawAppData['version'] === 'string') result.version = rawAppData['version'];
      if (typeof rawAppData['buildTime'] === 'string') result.buildTime = rawAppData['buildTime'];
      if (typeof rawAppData['env'] === 'string') result.env = rawAppData['env'];

      return result;
    } catch {
      return null;
    }
  }

  private getIntegrityRetryCount(): number {
    const value: string | null = sessionStorage.getItem(CACHE_INTEGRITY_KEY);
    return value ? parseInt(value, 10) : 0;
  }

  private setIntegrityRetryCount(count: number): void {
    sessionStorage.setItem(CACHE_INTEGRITY_KEY, count.toString());
  }

  static resetIntegrityRetryCount(): void {
    if (sessionStorage.getItem(INTEGRITY_RELOADING_KEY)) {
      sessionStorage.removeItem(INTEGRITY_RELOADING_KEY);
      return;
    }
    sessionStorage.removeItem(CACHE_INTEGRITY_KEY);
  }
}
